import { makeObservable, observable, runInAction } from 'mobx';
import { FieldState, FormState } from 'formstate';

import { FormValidators, setFormStateValues } from '@servicetitan/form-state';
import { inject, injectable } from '@servicetitan/react-ioc';
import { TableState, AsyncDataSource } from '@servicetitan/table';
import { DataResult, State } from '@servicetitan/data-query';

import { TABLE_PAGE_SIZE } from '../../utils/ux';
import { TelecomApi, TelecomPhoneNumber, UpdatePhoneRequest } from '../api/telecom.api';
import { PhoneData } from '../types/phone-data';
import { LoadStatus } from '../../../common/enums/load-status';
import { createPhoneId, parsePhoneId } from '../utils/phone-id.utils';
import { ManageTenantStore } from '../../stores/manage-tenant.store';
import { NotificationsStore } from '../../../common/stores/notifications.store';

interface PhoneFormState
    extends FormState<{
        id: FieldState<string>;
        phoneNumber: FieldState<string>;
        forwardTo: FieldState<string>;
        sms: FieldState<boolean>;
        system: FieldState<string>;
        portOutStatus: FieldState<string | null>;
        authName: FieldState<string>;
    }> {}

const sortFieldMapping: Record<keyof Omit<PhoneData, 'id'>, keyof TelecomPhoneNumber> = {
    phoneNumber: 'phoneNumberE164',
    forwardTo: 'forwardingNumberE164',
    sms: 'allowIncomingMessages',
    system: 'system',
    portOutStatus: 'portOutStatus',
    authName: 'authenticationName',
};

const mapTelecomPhoneNumberToPhoneData = (telecomPhone: TelecomPhoneNumber): PhoneData => {
    return {
        id: createPhoneId(telecomPhone),
        phoneNumber: telecomPhone.phoneNumberE164 || '',
        forwardTo: telecomPhone.forwardingNumberE164 || '',
        sms: telecomPhone.allowIncomingMessages ?? false,
        system: telecomPhone.system || '',
        portOutStatus: telecomPhone.portOutStatus || null,
        authName: telecomPhone.authenticationName || '',
    };
};

@injectable()
export class PhonesStore {
    @observable tableState: TableState<PhoneData, string>;
    @observable searchFilter = '';
    @observable editingPhoneId: string | null = null;
    @observable loadStatus = LoadStatus.None;
    initialised = false;

    constructor(
        @inject(TelecomApi) private telecomApi: TelecomApi,
        @inject(ManageTenantStore) private manageTenantStore: ManageTenantStore,
        @inject(NotificationsStore) private notificationsStore: NotificationsStore,
    ) {
        makeObservable(this);

        this.tableState = new TableState<PhoneData, string>({
            pageSize: TABLE_PAGE_SIZE,
            rowIdKey: 'id',
            getFormState: this.formStateInitializer,
        });
    }

    formStateInitializer = (row: PhoneData): PhoneFormState => {
        const formState = new FormState({
            id: new FieldState<string>(row.id || ''),
            forwardTo: new FieldState<string>(row.forwardTo || '')
                .validators(FormValidators.isMatchingRegex(/^\+1\d{10}$/, 'phone'))
                .disableAutoValidation(),
            sms: new FieldState<boolean>(row.sms || false),
        }) as PhoneFormState;

        return setFormStateValues(formState, row);
    };

    init = () => {
        if (!this.initialised) {
            this.initialised = true;
            this.tableState
                .setDataSource(
                    new AsyncDataSource({ get: this.fetchPhones }, (row: PhoneData) => row.id),
                )
                .catch(null);
        }
    };

    fetchPhones = async (state: State): Promise<DataResult<PhoneData>> => {
        runInAction(() => {
            this.loadStatus = LoadStatus.Loading;
        });

        if (!this.manageTenantStore.tenant) {
            return this.handleBeErrorForDataFetch();
        }

        const skip = state.skip ?? 0;
        const take = state.take ?? TABLE_PAGE_SIZE;
        const page = Math.floor(skip / TABLE_PAGE_SIZE) + 1;
        const pageSize = take;

        let sortField: string | undefined;
        let isAscending: boolean | undefined;

        if (state.sort?.[0]) {
            const field = state.sort[0].field;
            const backendField =
                field in sortFieldMapping
                    ? sortFieldMapping[field as keyof typeof sortFieldMapping]
                    : field;
            sortField = backendField.charAt(0).toUpperCase() + backendField.slice(1);
            isAscending = state.sort[0].dir === 'asc';
        }

        try {
            const response = await this.telecomApi.phoneNumbersGET(
                page,
                pageSize,
                isAscending,
                sortField,
                this.searchFilter,
                this.manageTenantStore.tenant.internalId,
                this.manageTenantStore.tenant.partnerId,
            );

            runInAction(() => {
                this.loadStatus = LoadStatus.Ok;
            });

            return {
                data: response.data?.items?.map(mapTelecomPhoneNumberToPhoneData) || [],
                total: response.data?.count || 0,
            };
        } catch {
            return this.handleBeErrorForDataFetch();
        }
    };

    filterBySearch = async (searchFilter: string) => {
        runInAction(() => {
            this.searchFilter = searchFilter;
        });

        await this.fetchTableDataFromStart();
    };

    fetchTableDataFromStart = () => this.tableState.fetchData({ newSkip: 0 });

    handleBeErrorForDataFetch = () => {
        runInAction(() => {
            this.loadStatus = LoadStatus.Error;
        });

        return {
            data: [],
            total: 0,
        };
    };

    savePhone = async (phoneData: PhoneData, formValues: { forwardTo: string; sms: boolean }) => {
        try {
            runInAction(() => {
                this.loadStatus = LoadStatus.Loading;
            });

            const { ownerId, phoneNumberE164 } = parsePhoneId(phoneData.id);

            const updateRequest = new UpdatePhoneRequest({
                ownerId,
                phoneNumberE164,
                forwardingNumberE164: formValues.forwardTo,
                allowIncomingMessages: formValues.sms,
            });

            await this.telecomApi.phoneNumbersPATCH(
                this.manageTenantStore.tenant!.internalId,
                this.manageTenantStore.tenant!.partnerId,
                updateRequest,
            );

            this.notificationsStore.notifyInSnackbar({ title: 'Changes are saved' });
            await this.tableState.fetchData();

            runInAction(() => {
                this.loadStatus = LoadStatus.Ok;
            });
        } catch (error) {
            runInAction(() => {
                this.loadStatus = LoadStatus.Error;
            });
            throw error;
        }
    };

    deallocatePhone = async (phoneData: PhoneData) => {
        try {
            runInAction(() => {
                this.loadStatus = LoadStatus.Loading;
            });

            const { ownerId, phoneNumberE164 } = parsePhoneId(phoneData.id);

            await this.telecomApi.owner(
                ownerId,
                phoneNumberE164,
                this.manageTenantStore.tenant!.internalId,
                this.manageTenantStore.tenant!.partnerId,
            );

            this.notificationsStore.notifyInSnackbar({ title: 'Phone Number Deallocated' });
            // Refresh current page instead of going back to page 1
            await this.tableState.fetchData();

            runInAction(() => {
                this.loadStatus = LoadStatus.Ok;
            });
        } catch (error) {
            runInAction(() => {
                this.loadStatus = LoadStatus.Error;
            });
            throw error;
        }
    };
}
